import { headerAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { oauthSetupSteps } from "../oauthSetupSteps";
import { oauthAuth } from "../standardAuthPatterns";
import logo from "./logo.svg";

const credential = productVariant({
  authLabel: "Messaging API token",
  identifier: "line",
  keywords: "line messaging api bot official account japan chat push messages",
  protocol: "http",
  // Message content and rich-menu images are served from api-data.line.me,
  // which takes the same channel access token as api.line.me.
  defaultUrlPattern: () => "https://api(-data)?\\.line\\.me/.*",
  auth: (app) =>
    headerAuth([
      {
        name: "Authorization",
        value: app.secret.channelAccessToken,
        scheme: "Bearer",
      },
    ]),
  steps: [
    {
      title: "Create a LINE official account and its Messaging API channel",
      items: [
        {
          kind: "markdown",
          content:
            "Messaging API channels can no longer be created directly in the developers console. Sign up for a [LINE official account](https://account.line.biz/signup), then in [LINE Official Account Manager](https://manager.line.biz/) open **Settings › Messaging API** and assign the account to a provider — that creates the channel for you.",
        },
      ],
    },
    {
      title: "Issue a channel access token",
      items: [
        {
          kind: "markdown",
          content:
            "In the [LINE Developers console](https://developers.line.biz/console/), open the channel and issue a long-lived token from the **Messaging API** tab. A channel can only have one long-lived token at a time, and issuing a new one invalidates the old.",
        },
        {
          kind: "input",
          label: "Channel access token",
          required: true,
          target: { kind: "secrets", field: "channelAccessToken" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: () => "https://api.line.me/v2/bot/info",
  },
});

export const line = defineProduct({
  name: "LINE",
  tagLine: "Messaging for LINE official accounts",
  logo,
  tags: ["communication"],
  variants: [
    credential,
    productVariant({
      identifier: "lineOAuth",
      authLabel: "OAuth",
      protocol: "http",
      defaultUrlPattern: () =>
        "https://api\\.line\\.me/(?:v2/profile(?:\\?.*)?|oauth2/v2\\.1/userinfo(?:\\?.*)?)$",
      auth: oauthAuth,
      steps: oauthSetupSteps({
        identifier: "lineOAuth",
        registrationUrl: "https://developers.line.biz/console/",
        scopes: ["profile"],
        instructions:
          "Create a LINE Login channel. User tokens provide profile access; use the Messaging API token option for official account messages.",
      }),
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () =>
          "https://access.line.me/oauth2/v2.1/authorize",
        tokenEndpoint: () => "https://api.line.me/oauth2/v2.1/token",
        tokenEndpointAuthMethod: () => "client_secret_post",
        clientCredentialsFrom: "db",
        scopes: () => ["profile"],
        pkce: () => true,
      },
      connectionTest: {
        protocol: "http",
        request: () => "https://api.line.me/v2/profile",
      },
    }),
  ],
});
